import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { importMappings, importMappingsWorkbook } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { ImportMappingsResult, MappingImportMode } from '../../api/types';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { parseMappingsJson, type ParseResult } from './mappingsJson';

const workbookExtension = /\.xlsx$/i;

export interface ImportMappingsDialogProps {
  onClose: () => void;
}

/**
 * One dialog for both shapes of a mappings file. A JSON file, or pasted JSON, is checked in the
 * browser and sent as the import body; an Excel workbook goes to the server as it is, because only
 * the server reads spreadsheets. Either way the same import runs, with the mode chosen here.
 */
export function ImportMappingsDialog({ onClose }: ImportMappingsDialogProps) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [pasted, setPasted] = useState('');
  const [mode, setMode] = useState<MappingImportMode>('Merge');

  const isWorkbook = file !== null && workbookExtension.test(file.name);
  // A picked JSON file is read into the same text the paste field holds, so one parser serves both.
  const parsed: ParseResult | null = useMemo(
    () => (isWorkbook || pasted.trim() === '' ? null : parseMappingsJson(pasted)),
    [isWorkbook, pasted],
  );

  const mutation = useMutation({
    mutationFn: (): Promise<ImportMappingsResult> => {
      if (isWorkbook && file) return importMappingsWorkbook(file, mode);
      if (parsed?.ok) return importMappings({ mappings: parsed.mappings, mode });
      return Promise.reject(new Error('Nothing to import.'));
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mappings.all });
      toastSuccess(t('mappings.import.result', { ...result }));
      onClose();
    },
    onError: toastError,
  });

  const pickFile = async (picked: File | null) => {
    setFile(picked);
    if (picked && !workbookExtension.test(picked.name)) {
      setPasted(await picked.text());
    }
  };

  const canSubmit = !mutation.isPending && (isWorkbook || parsed?.ok === true);

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('mappings.import.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <DialogContentText>{t('mappings.import.help')}</DialogContentText>

          <Stack direction="row" spacing={2} alignItems="center">
            <input
              ref={fileInput}
              type="file"
              accept=".json,.xlsx,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(event) => void pickFile(event.target.files?.[0] ?? null)}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInput.current?.click()}
              disabled={mutation.isPending}
            >
              {t('mappings.import.chooseFile')}
            </Button>
            {file && (
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {file.name}
              </Typography>
            )}
          </Stack>

          {isWorkbook ? (
            <Alert severity="info">{t('mappings.import.xlsxReady', { name: file?.name ?? '' })}</Alert>
          ) : (
            <TextField
              label={t('mappings.import.paste')}
              value={pasted}
              onChange={(event) => setPasted(event.target.value)}
              multiline
              minRows={8}
              maxRows={18}
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: 13 } } }}
            />
          )}

          {parsed && !parsed.ok && (
            <Alert severity="error">{t(parsed.reasonKey, { detail: parsed.detail ?? '' })}</Alert>
          )}
          {parsed?.ok && (
            <Alert severity="success">
              {t('mappings.import.jsonReady', { count: parsed.mappings.length })}
            </Alert>
          )}

          <FormControl>
            <FormLabel>{t('mappings.import.mode.label')}</FormLabel>
            <RadioGroup value={mode} onChange={(event) => setMode(event.target.value as MappingImportMode)}>
              <FormControlLabel value="Merge" control={<Radio />} label={t('mappings.import.mode.merge')} />
              <FormControlLabel
                value="Replace"
                control={<Radio />}
                label={t('mappings.import.mode.replace')}
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          {t('actions.cancel')}
        </Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={!canSubmit}>
          {t('mappings.import.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
